import { DateTimePicker, LocalizationProvider } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import {
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Grid,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import axios from "axios";
import * as React from "react";

import { LoadingWrapper } from "../../../components/LoadingWrapper";
import { Config } from "../../../config/config";
import { Helpers } from "../../../utils/helpers";
import { PopupMessage } from "../../../utils/message";

const getDefaultStatsEndTime = () => new Date();
const getDefaultStatsStartTime = (endTime = getDefaultStatsEndTime()) =>
    new Date(endTime.getTime() - 24 * 60 * 60 * 1000);

interface DailyStatsResponse {
    success: boolean;
    message?: string;
    window?: {
        timezone: string;
        boundaryHour: number;
        from: string;
        to: string;
        fromLocal: string;
        toLocal: string;
        label: string;
    };
    preparations?: {
        created: number;
        byStatus: Record<string, number>;
        layoutDone: number;
        participantsDone: number;
        ready: number;
        enlistedSlots: number;
        uniqueUsersEnlisted: number;
        uniqueSquads: number;
    };
    wars?: {
        created: number;
        byStatus: Record<string, number>;
        byResult: Record<string, number>;
        endedInWindow: number;
        uniqueSquads: number;
        uniqueUsersInWarRoster: number;
        totalStars: number;
        avgStarsPerWar: number;
    };
    battles?: {
        created: number;
        byStatus: Record<string, number>;
        completedInWindow: number;
        wins: number;
        uniqueAttackers: number;
        uniqueSquads: number;
        totalStars: number;
        avgStars: number;
        avgPercent: number;
    };
}

const formatCountMap = (map?: Record<string, number>) => {
    if (!map || Object.keys(map).length === 0) {
        return "—";
    }
    return Object.entries(map)
        .map(([key, value]) => `${key}: ${value}`)
        .join(" · ");
};

const formatCountMapLines = (map?: Record<string, number>, indent = "  ") => {
    if (!map || Object.keys(map).length === 0) {
        return `${indent}- (none)`;
    }
    return Object.entries(map)
        .map(([key, value]) => `${indent}- ${key}: ${value}`)
        .join("\n");
};

const valueOrDash = (value: any) => {
    if (value === null || value === undefined || value === "") {
        return "—";
    }
    return String(value);
};

/** Plain-text report suitable to paste into a Telegram group. */
const formatDailyStatsForTelegram = (stats: DailyStatsResponse) => {
    const prep = stats.preparations;
    const wars = stats.wars;
    const battles = stats.battles;

    return [
        "Alliance War Daily Stats",
        `Window: ${valueOrDash(stats.window?.label)}`,
        "",
        "Preparations",
        `- Created: ${valueOrDash(prep?.created)}`,
        "- By status:",
        formatCountMapLines(prep?.byStatus),
        `- Ready (layout + participants): ${valueOrDash(prep?.ready)}`,
        `- Layout done: ${valueOrDash(prep?.layoutDone)}`,
        `- Participants done: ${valueOrDash(prep?.participantsDone)}`,
        `- Enlisted slots: ${valueOrDash(prep?.enlistedSlots)}`,
        `- Unique users enlisted: ${valueOrDash(prep?.uniqueUsersEnlisted)}`,
        `- Unique squads: ${valueOrDash(prep?.uniqueSquads)}`,
        "",
        "Wars",
        `- Created: ${valueOrDash(wars?.created)}`,
        `- Ended: ${valueOrDash(wars?.endedInWindow)}`,
        "- By status:",
        formatCountMapLines(wars?.byStatus),
        "- By result:",
        formatCountMapLines(wars?.byResult),
        `- Unique squads: ${valueOrDash(wars?.uniqueSquads)}`,
        `- Unique users in war roster: ${valueOrDash(wars?.uniqueUsersInWarRoster)}`,
        `- Total stars: ${valueOrDash(wars?.totalStars)}`,
        `- Avg stars / war: ${valueOrDash(wars?.avgStarsPerWar)}`,
        "",
        "Battles",
        `- Created: ${valueOrDash(battles?.created)}`,
        `- Completed: ${valueOrDash(battles?.completedInWindow)}`,
        "- By status:",
        formatCountMapLines(battles?.byStatus),
        `- Wins: ${valueOrDash(battles?.wins)}`,
        `- Unique attackers: ${valueOrDash(battles?.uniqueAttackers)}`,
        `- Unique squads: ${valueOrDash(battles?.uniqueSquads)}`,
        `- Total stars: ${valueOrDash(battles?.totalStars)}`,
        `- Avg stars: ${valueOrDash(battles?.avgStars)}`,
        `- Avg percent: ${valueOrDash(battles?.avgPercent)}`,
    ].join("\n");
};

const StatItem = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <Grid item xs={12} sm={6} md={4}>
        <Typography variant="caption" color="textSecondary" display="block">
            {label}
        </Typography>
        <Typography variant="body1">{value ?? "—"}</Typography>
    </Grid>
);

interface AllianceWarClanSide {
    id: string | null;
    name: string | null;
    member: number | null;
    stars: number;
}

interface AllianceWarListItem {
    id: string;
    seasonId: string | null;
    status: string | null;
    result: string | null;
    attacker: AllianceWarClanSide;
    defender: AllianceWarClanSide;
    startedAt: string | null;
    endedAt: string | null;
    createdAt: string | null;
}

interface AllianceWarListResponse {
    success: boolean;
    data: {
        items: AllianceWarListItem[];
        total: number;
        page: number;
        pageSize: number;
    };
}

const initialFilters = {
    query: "",
    clan: "",
    seasonId: "",
    status: "",
    page: 1,
    pageSize: 10,
};

const renderStatus = (status?: string | null) => {
    const normalized = String(status || "unknown").toUpperCase();
    const styles = {
        ENDED: { backgroundColor: "#2e7d32", color: "#fff" },
        ONGOING: { backgroundColor: "#ed6c02", color: "#fff" },
    };

    return <Chip
        size="small"
        label={status || "unknown"}
        style={styles[normalized] || { backgroundColor: "#757575", color: "#fff" }}
    />;
};

const renderClan = (clan?: AllianceWarClanSide) => {
    if (!clan) {
        return "n/a";
    }

    return <React.Fragment>
        <Typography variant="body2">{clan.name || "Unknown clan"}</Typography>
        <Typography variant="caption" color="textSecondary">
            {clan.id || "n/a"}{clan.member != null ? ` · ${clan.member} members` : ""}
        </Typography>
    </React.Fragment>;
};

export const AllianceWarAlliances = (props: any) => {
    const [filters, setFilters] = React.useState(initialFilters);
    const [response, setResponse] = React.useState<AllianceWarListResponse | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [syncing, setSyncing] = React.useState(false);
    const [loadingDailyStats, setLoadingDailyStats] = React.useState(false);
    const [dailyStatsDateOpen, setDailyStatsDateOpen] = React.useState(false);
    const [dailyStatsOpen, setDailyStatsOpen] = React.useState(false);
    const [dailyStatsStartDate, setDailyStatsStartDate] = React.useState<Date | null>(
        getDefaultStatsStartTime(),
    );
    const [dailyStatsEndDate, setDailyStatsEndDate] = React.useState<Date | null>(
        getDefaultStatsEndTime(),
    );
    const [dailyStats, setDailyStats] = React.useState<DailyStatsResponse | null>(null);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        document.title = Helpers.documentTitle("Alliance Wars / Alliances");
        loadList(initialFilters);
    }, []);

    const loadList = (nextFilters = filters) => {
        setLoading(true);
        setError(null);

        axios.get<AllianceWarListResponse>(`${Config.getInstance.apiUrl}/api/alliance_wars/wars`, {
            params: nextFilters,
            headers: {
                Authorization: Helpers.getSessionId(),
                env: props.activeEnv,
            },
        })
            .then((res) => {
                setResponse(res.data);
                setFilters(nextFilters);
            })
            .catch((err) => {
                setError(err?.response?.data?.message || err?.message || "Couldn't load alliance wars.");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleFilterChange = (key: string) => (event) => {
        setFilters({
            ...filters,
            [key]: event.target.value,
        });
    };

    const handleSearch = () => {
        loadList({
            ...filters,
            page: 1,
        });
    };

    const handleClear = () => {
        loadList(initialFilters);
    };

    const handleSync = async () => {
        if (syncing) {
            return;
        }

        setSyncing(true);

        try {
            const res = await axios.post(
                `${Config.getInstance.apiUrl}/api/alliance_wars/sync-volunteers`,
                {},
                {
                    headers: {
                        Authorization: Helpers.getSessionId(),
                        env: props.activeEnv,
                    },
                },
            );

            const data = res.data?.data;
            const removedCount = data?.removedCount ?? 0;
            const scannedKeys = data?.scannedKeys ?? 0;

            PopupMessage.success(
                `Removed ${removedCount} orphan volunteer${removedCount === 1 ? "" : "s"} from Redis (scanned ${scannedKeys} keys)`,
            );
        } catch (err: any) {
            PopupMessage.error(
                err?.response?.data?.error || err?.response?.data?.message || err?.message || "Failed to sync volunteers",
            );
        } finally {
            setSyncing(false);
        }
    };

    const openDailyStatsDatePicker = () => {
        if (loadingDailyStats) {
            return;
        }
        setDailyStatsDateOpen(true);
    };

    const closeDailyStatsDatePicker = () => {
        if (loadingDailyStats) {
            return;
        }
        setDailyStatsDateOpen(false);
    };

    const resetStatsStartDate = () => {
        if (!dailyStatsEndDate || Number.isNaN(dailyStatsEndDate.getTime())) {
            PopupMessage.error("Please select a valid end time first");
            return;
        }
        setDailyStatsStartDate(getDefaultStatsStartTime(dailyStatsEndDate));
    };

    const handleGetDailyStats = async () => {
        if (loadingDailyStats) {
            return;
        }

        if (
            !dailyStatsStartDate
            || !dailyStatsEndDate
            || Number.isNaN(dailyStatsStartDate.getTime())
            || Number.isNaN(dailyStatsEndDate.getTime())
        ) {
            PopupMessage.error("Please select valid start and end times");
            return;
        }
        if (dailyStatsStartDate >= dailyStatsEndDate) {
            PopupMessage.error("Start time must be before end time");
            return;
        }

        setLoadingDailyStats(true);
        setDailyStatsDateOpen(false);
        setDailyStatsOpen(true);

        try {
            const res = await axios.get(
                `${Config.getInstance.apiUrl}/api/alliance_wars/daily_stats`,
                {
                    params: {
                        from: dailyStatsStartDate.toISOString(),
                        to: dailyStatsEndDate.toISOString(),
                    },
                    headers: {
                        Authorization: Helpers.getSessionId(),
                        env: props.activeEnv,
                    },
                },
            );

            if (!res.data?.success) {
                PopupMessage.error(res.data?.message || "Failed to load daily stats");
                setDailyStats(null);
                return;
            }

            setDailyStats(res.data);
        } catch (err: any) {
            PopupMessage.error(
                err?.response?.data?.message || err?.message || "Failed to load daily stats",
            );
            setDailyStats(null);
        } finally {
            setLoadingDailyStats(false);
        }
    };

    const handlePageChange = (_event: unknown, page: number) => {
        loadList({
            ...filters,
            page: page + 1,
        });
    };

    const handleRowsPerPageChange = (event) => {
        loadList({
            ...filters,
            page: 1,
            pageSize: parseInt(event.target.value, 10),
        });
    };

    const items = response?.data.items || [];
    const page = (response?.data.page || 1) - 1;
    const pageSize = response?.data.pageSize || filters.pageSize;
    const total = response?.data.total || 0;

    return <div className="contentPadding">
        <Paper className="root" elevation={0}>
            <div style={{ float: "left", padding: "15px 22px", width: "100%" }}>
                <div style={{ float: "left" }}>
                    <Typography variant="h6">Alliance wars / Alliances</Typography>
                </div>
                <div style={{ float: "right" }}>
                    <Button
                        disableElevation
                        variant="outlined"
                        color="primary"
                        onClick={openDailyStatsDatePicker}
                        disabled={loadingDailyStats}
                        style={{ marginRight: 8 }}
                    >
                        {loadingDailyStats ? "Loading stats..." : "Daily Stats"}
                    </Button>
                    <Button
                        disableElevation
                        variant="contained"
                        color="primary"
                        onClick={handleSync}
                        disabled={syncing}
                    >
                        {syncing ? "Syncing..." : "Sync"}
                    </Button>
                </div>
            </div>
        </Paper>

        <Dialog
            open={dailyStatsDateOpen}
            onClose={closeDailyStatsDatePicker}
            maxWidth="xs"
            fullWidth
            aria-labelledby="daily-stats-date-dialog-title"
        >
            <DialogTitle id="daily-stats-date-dialog-title">
                Select stats range
            </DialogTitle>
            <DialogContent>
                <DialogContentText style={{ marginBottom: 16 }}>
                    Select the exact start and end times for the stats.
                </DialogContentText>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DateTimePicker
                        label="Start time"
                        value={dailyStatsStartDate}
                        onChange={(value) => setDailyStatsStartDate(value)}
                        maxDateTime={dailyStatsEndDate || new Date()}
                        renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                    <Button
                        onClick={resetStatsStartDate}
                        disabled={loadingDailyStats || !dailyStatsEndDate}
                        size="small"
                        style={{ marginTop: 8, marginBottom: 16 }}
                    >
                        Reset start to 24h before end
                    </Button>
                    <DateTimePicker
                        label="End time"
                        value={dailyStatsEndDate}
                        onChange={(value) => setDailyStatsEndDate(value)}
                        maxDateTime={new Date()}
                        renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                </LocalizationProvider>
            </DialogContent>
            <DialogActions>
                <Button onClick={closeDailyStatsDatePicker} disabled={loadingDailyStats}>
                    Cancel
                </Button>
                <Button
                    onClick={handleGetDailyStats}
                    color="primary"
                    variant="contained"
                    disableElevation
                    disabled={loadingDailyStats || !dailyStatsStartDate || !dailyStatsEndDate}
                    autoFocus
                >
                    {loadingDailyStats ? "Loading..." : "Get Stats"}
                </Button>
            </DialogActions>
        </Dialog>

        <Dialog
            open={dailyStatsOpen}
            onClose={() => !loadingDailyStats && setDailyStatsOpen(false)}
            maxWidth="md"
            fullWidth
            aria-labelledby="daily-stats-dialog-title"
        >
            <DialogTitle id="daily-stats-dialog-title">
                Alliance War Daily Stats
            </DialogTitle>
            <DialogContent dividers>
                {loadingDailyStats && !dailyStats ? (
                    <Typography>Loading...</Typography>
                ) : !dailyStats ? (
                    <Typography color="error">No stats loaded</Typography>
                ) : (
                    <React.Fragment>
                        <Typography variant="subtitle1" gutterBottom>
                            {dailyStats.window?.label || "—"}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            Stats for the selected start and end times (Asia/Bangkok).
                        </Typography>

                        <Divider style={{ margin: "16px 0" }} />
                        <Typography variant="h6" gutterBottom>Preparations</Typography>
                        <Grid container spacing={2}>
                            <StatItem label="Created" value={dailyStats.preparations?.created} />
                            <StatItem label="By status" value={formatCountMap(dailyStats.preparations?.byStatus)} />
                            <StatItem label="Ready (layout + participants)" value={dailyStats.preparations?.ready} />
                            <StatItem label="Layout done" value={dailyStats.preparations?.layoutDone} />
                            <StatItem label="Participants done" value={dailyStats.preparations?.participantsDone} />
                            <StatItem label="Enlisted slots" value={dailyStats.preparations?.enlistedSlots} />
                            <StatItem label="Unique users enlisted" value={dailyStats.preparations?.uniqueUsersEnlisted} />
                            <StatItem label="Unique squads" value={dailyStats.preparations?.uniqueSquads} />
                        </Grid>

                        <Divider style={{ margin: "16px 0" }} />
                        <Typography variant="h6" gutterBottom>Wars</Typography>
                        <Grid container spacing={2}>
                            <StatItem label="Created" value={dailyStats.wars?.created} />
                            <StatItem label="Ended" value={dailyStats.wars?.endedInWindow} />
                            <StatItem label="By status" value={formatCountMap(dailyStats.wars?.byStatus)} />
                            <StatItem label="By result" value={formatCountMap(dailyStats.wars?.byResult)} />
                            <StatItem label="Unique squads" value={dailyStats.wars?.uniqueSquads} />
                            <StatItem label="Unique users in war roster" value={dailyStats.wars?.uniqueUsersInWarRoster} />
                            <StatItem label="Total stars" value={dailyStats.wars?.totalStars} />
                            <StatItem label="Avg stars / war" value={dailyStats.wars?.avgStarsPerWar} />
                        </Grid>

                        <Divider style={{ margin: "16px 0" }} />
                        <Typography variant="h6" gutterBottom>Battles</Typography>
                        <Grid container spacing={2}>
                            <StatItem label="Created" value={dailyStats.battles?.created} />
                            <StatItem label="Completed" value={dailyStats.battles?.completedInWindow} />
                            <StatItem label="By status" value={formatCountMap(dailyStats.battles?.byStatus)} />
                            <StatItem label="Wins" value={dailyStats.battles?.wins} />
                            <StatItem label="Unique attackers" value={dailyStats.battles?.uniqueAttackers} />
                            <StatItem label="Unique squads" value={dailyStats.battles?.uniqueSquads} />
                            <StatItem label="Total stars" value={dailyStats.battles?.totalStars} />
                            <StatItem label="Avg stars" value={dailyStats.battles?.avgStars} />
                            <StatItem label="Avg percent" value={dailyStats.battles?.avgPercent} />
                        </Grid>
                    </React.Fragment>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => {
                        setDailyStatsOpen(false);
                        openDailyStatsDatePicker();
                    }}
                    disabled={loadingDailyStats}
                    color="primary"
                >
                    Change date
                </Button>
                <Button
                    onClick={handleGetDailyStats}
                    disabled={loadingDailyStats}
                    color="primary"
                >
                    {loadingDailyStats ? "Refreshing..." : "Refresh"}
                </Button>
                <Button
                    onClick={async () => {
                        if (!dailyStats) {
                            PopupMessage.error("No stats to copy");
                            return;
                        }
                        const text = formatDailyStatsForTelegram(dailyStats);
                        try {
                            await navigator.clipboard.writeText(text);
                            PopupMessage.success("Copied Telegram report to clipboard");
                        } catch (err: any) {
                            PopupMessage.error(err?.message || "Failed to copy");
                        }
                    }}
                    disabled={loadingDailyStats || !dailyStats}
                    color="secondary"
                    variant="outlined"
                >
                    Copy for Telegram
                </Button>
                <Button
                    onClick={() => setDailyStatsOpen(false)}
                    disabled={loadingDailyStats}
                    variant="contained"
                    disableElevation
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>

        <Paper className="root" elevation={0} style={{ marginTop: 16 }}>
            <div style={{ float: "left", width: "100%", padding: "22px 22px 0 22px" }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label="Search"
                            value={filters.query}
                            onChange={handleFilterChange("query")}
                            placeholder="War ID, season ID, clan"
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label="Clan"
                            value={filters.clan}
                            onChange={handleFilterChange("clan")}
                            placeholder="Clan name or squad ID"
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label="Season ID"
                            value={filters.seasonId}
                            onChange={handleFilterChange("seasonId")}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            select
                            label="Status"
                            value={filters.status}
                            onChange={handleFilterChange("status")}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="ONGOING">Ongoing</MenuItem>
                            <MenuItem value="ENDED">Ended</MenuItem>
                        </TextField>
                    </Grid>
                </Grid>

                <div style={{ marginTop: 16, marginBottom: 16 }}>
                    <Button disableElevation variant="contained" color="primary" onClick={handleSearch}>
                        Search
                    </Button>
                    <Button disableElevation variant="contained" color="secondary" onClick={handleClear} style={{ marginLeft: 8 }}>
                        Clear
                    </Button>
                </div>
            </div>

            <div style={{ float: "left", width: "100%", padding: "0 22px 22px 22px" }}>
                <LoadingWrapper loading={loading} error={error}>
                    {items.length === 0 ? <div style={{ padding: "24px 0" }}>
                        <Typography variant="subtitle1">No alliance wars found</Typography>
                        <Typography variant="body2" color="textSecondary">
                            Try a broader filter set.
                        </Typography>
                    </div> : <React.Fragment>
                        <Table className="min-table-size">
                            <TableHead>
                                <TableRow>
                                    <TableCell>War ID</TableCell>
                                    <TableCell>Season</TableCell>
                                    <TableCell>Attacker clan</TableCell>
                                    <TableCell>Defender clan</TableCell>
                                    <TableCell>Stars</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Result</TableCell>
                                    <TableCell>Started at</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map((item) => <TableRow hover key={item.id}>
                                    <TableCell>{item.id}</TableCell>
                                    <TableCell>{item.seasonId || "n/a"}</TableCell>
                                    <TableCell>{renderClan(item.attacker)}</TableCell>
                                    <TableCell>{renderClan(item.defender)}</TableCell>
                                    <TableCell>
                                        {(item.attacker?.stars ?? 0)} - {(item.defender?.stars ?? 0)}
                                    </TableCell>
                                    <TableCell>{renderStatus(item.status)}</TableCell>
                                    <TableCell>{item.result || "n/a"}</TableCell>
                                    <TableCell>
                                        {item.startedAt ? Helpers.formatDateWithTime(item.startedAt, true) : "n/a"}
                                    </TableCell>
                                </TableRow>)}
                            </TableBody>
                        </Table>

                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={handlePageChange}
                            rowsPerPage={pageSize}
                            onRowsPerPageChange={handleRowsPerPageChange}
                            rowsPerPageOptions={[5, 10, 20]}
                        />
                    </React.Fragment>}
                </LoadingWrapper>
            </div>
        </Paper>
    </div>;
};
